import type { MaybeRefOrGetter } from 'vue';
import type { AnyItem, FieldSelection, GlobalCollectionId, PrimaryKey, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { computed, ref, toValue, watch } from 'vue';
import { useProcessedFieldSelection } from '#layers/access/public';
import { isNil } from '#layers/base/public';
import { toCompositePrimaryKey } from '#layers/data-model/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';

export interface UseItemOptions {
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | GlobalCollectionId | null | undefined>;
	itemKey: MaybeRefOrGetter<PrimaryKey | null | undefined>;
	selection?: MaybeRefOrGetter<FieldSelection | undefined>;

	enabled?: MaybeRefOrGetter<boolean>;
	onError?: (error: unknown) => void;
}

interface ReadOneQuery extends FieldSelection {
	key: string | number;
}

export function useItem<T extends AnyItem>(options: UseItemOptions) {
	const { collection, itemKey, selection, enabled, onError } = options;
	const { findCollectionById } = useWorkspaceSchemaStore();
	const store = useWorkspaceEntityStore();
	const isLoading = ref(false);
	const error = ref<unknown | null>(null);

	const computedCollection = computed(() => {
		const collectionOrId = toValue(collection);

		if (typeof collectionOrId === 'string') {
			return findCollectionById(collectionOrId) ?? undefined;
		}
		else {
			return collectionOrId ?? undefined;
		}
	});

	const storeApi = computed(() => computedCollection.value?.apiName ? store.$collection(computedCollection.value.apiName) : undefined);

	const compositeItemKey = computed(() => {
		const key = toValue(itemKey);

		if (!computedCollection.value || isNil(key)) {
			return;
		}

		return toCompositePrimaryKey(key, computedCollection.value);
	});
	const storeItemKey = computed(() => storeApi.value && compositeItemKey.value ? storeApi.value.getKey(compositeItemKey.value) : undefined);

	const { selection: processedSelection } = useProcessedFieldSelection({
		collection: computedCollection,
		selection,
	});

	const query = computed<ReadOneQuery | null>(() => {
		if (storeItemKey.value) {
			return {
				key: storeItemKey.value,
				...processedSelection.value,
			};
		}

		return null;
	});

	const item = computed<T | null>(() => {
		if (!computedCollection.value || !storeItemKey.value || !storeApi.value) {
			return null;
		}

		return storeApi.value.peekFirst(storeItemKey.value) ?? null;
	});

	watch([() => query.value?.key, () => toValue(enabled), storeApi, () => toValue(selection)], async ([itemKey, enabled, storeApi]) => {
		if (!enabled || !itemKey || !storeApi) {
			return;
		}

		isLoading.value = true;

		try {
			await storeApi.findFirst({
				key: itemKey,
				fields: query.value?.fields,
				include: query.value?.include,
				fetchPolicy: 'fetch-only',
			});
		}
		catch (error_) {
			onError?.(error_);
			error.value = error_;
		}
		finally {
			isLoading.value = false;
		}
	}, {
		immediate: true,
	});

	return {
		collection: computedCollection,
		itemKey: compositeItemKey,
		item,
		query,
		isLoading,
		error,
	};
}
